import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

import { UsersApi } from '@core/api';
import * as Entities from '@shared/entities';
import { ListResponse, Result } from '@shared/models';
import { extractErrorMessage } from '@shared/utils';
import { commandResult, emptyListResponse, failureResult } from './store-helpers';

type UsersState = {
  response: ListResponse<Entities.User>;
  loading: boolean;
  loaded: boolean;
  roles: Entities.Role[];
  loadingRoles: boolean;
  loadedRoles: boolean;
  selectedUser: Entities.User | null;
  selectedUserId: number | null;
  loadingSelectedUser: boolean;
  commandInProgress: boolean;
  lastCommandName: string | null;
  lastCommandResult: Result<unknown> | null;
  errorMessage: string | null;
};

const initialUsersState: UsersState = {
  response: emptyListResponse<Entities.User>(),
  loading: false,
  loaded: false,
  roles: [],
  loadingRoles: false,
  loadedRoles: false,
  selectedUser: null,
  selectedUserId: null,
  loadingSelectedUser: false,
  commandInProgress: false,
  lastCommandName: null,
  lastCommandResult: null,
  errorMessage: null,
};

export const UsersStore = signalStore(
  { providedIn: 'root' },
  withState(initialUsersState),
  withComputed(({ response, loading, loadingRoles, loadingSelectedUser, commandInProgress }) => ({
    records: computed(() => response().records),
    byId: computed(() => new Map(response().records.map((user) => [user.userId, user]))),
    isBusy: computed(() => loading() || loadingRoles() || loadingSelectedUser() || commandInProgress()),
  })),
  withMethods((store, usersApi = inject(UsersApi)) => {
    let loadUsersRequestId = 0;
    let loadRolesRequestId = 0;
    let loadSelectedUserRequestId = 0;

    const loadUsers = (force = false): void => {
      if (!force && store.loaded()) {
        return;
      }

      const requestId = ++loadUsersRequestId;
      patchState(store, { loading: true, errorMessage: null });

      void usersApi
        .loadUsers(force)
        .then((response) => {
          if (requestId === loadUsersRequestId) {
            patchState(store, { response, loaded: true });
          }
        })
        .catch((error: unknown) => {
          if (requestId === loadUsersRequestId) {
            patchState(store, { errorMessage: extractErrorMessage(error, 'Unable to load users.') });
          }
        })
        .finally(() => {
          if (requestId === loadUsersRequestId) {
            patchState(store, { loading: false });
          }
        });
    };

    const loadRoles = (force = false): void => {
      if (!force && store.loadedRoles()) {
        return;
      }

      const requestId = ++loadRolesRequestId;
      patchState(store, { loadingRoles: true, errorMessage: null });

      void firstValueFrom(usersApi.getRoles())
        .then((roles) => {
          if (requestId === loadRolesRequestId) {
            patchState(store, { roles, loadedRoles: true });
          }
        })
        .catch((error: unknown) => {
          if (requestId === loadRolesRequestId) {
            patchState(store, { errorMessage: extractErrorMessage(error, 'Unable to load roles.') });
          }
        })
        .finally(() => {
          if (requestId === loadRolesRequestId) {
            patchState(store, { loadingRoles: false });
          }
        });
    };

    const loadUserByUserId = (userId: number): void => {
      const requestId = ++loadSelectedUserRequestId;
      patchState(store, { loadingSelectedUser: true, selectedUserId: userId, errorMessage: null });

      void firstValueFrom(usersApi.getUserByUserId(userId))
        .then((selectedUser) => {
          if (requestId === loadSelectedUserRequestId) {
            patchState(store, { selectedUser });
          }
        })
        .catch((error: unknown) => {
          if (requestId === loadSelectedUserRequestId) {
            patchState(store, {
              selectedUser: null,
              errorMessage: extractErrorMessage(error, 'Unable to load the requested user.'),
            });
          }
        })
        .finally(() => {
          if (requestId === loadSelectedUserRequestId) {
            patchState(store, { loadingSelectedUser: false });
          }
        });
    };

    const createUser = (user: Entities.User): void => {
      if (store.commandInProgress()) {
        return;
      }

      patchState(store, {
        commandInProgress: true,
        lastCommandName: 'createUser',
        lastCommandResult: null,
        errorMessage: null,
      });

      void usersApi
        .createUser(user)
        .then((successful) => {
          const result = successful ? Result.success(true) : failureResult('Unable to create user.', false);
          patchState(store, { lastCommandResult: commandResult('createUser', result) });
          if (successful) {
            loadUsers(true);
          } else {
            patchState(store, { errorMessage: result.message });
          }
        })
        .catch((error: unknown) => {
          const message = extractErrorMessage(error, 'Unable to create user.');
          patchState(store, {
            errorMessage: message,
            lastCommandResult: commandResult('createUser', failureResult(message, false)),
          });
        })
        .finally(() => {
          patchState(store, { commandInProgress: false });
        });
    };

    const updateUser = (user: Entities.User): void => {
      if (store.commandInProgress()) {
        return;
      }

      patchState(store, {
        commandInProgress: true,
        lastCommandName: 'updateUser',
        lastCommandResult: null,
        errorMessage: null,
      });

      void usersApi
        .updateUser(user)
        .then((updatedUser) => {
          const result = updatedUser ? Result.success(updatedUser) : failureResult('Unable to update user.', undefined);
          patchState(store, {
            selectedUser: updatedUser ?? store.selectedUser(),
            lastCommandResult: commandResult('updateUser', result),
            errorMessage: updatedUser ? null : result.message,
          });
          if (updatedUser) {
            loadUsers(true);
          }
        })
        .catch((error: unknown) => {
          const message = extractErrorMessage(error, 'Unable to update user.');
          patchState(store, {
            errorMessage: message,
            lastCommandResult: commandResult('updateUser', failureResult(message)),
          });
        })
        .finally(() => {
          patchState(store, { commandInProgress: false });
        });
    };

    return {
      loadUsers,
      loadRoles,
      loadUserByUserId,
      createUser,
      updateUser,
      clearSelectedUser(): void {
        patchState(store, { selectedUser: null, selectedUserId: null });
      },
      clearCommandResult(): void {
        patchState(store, { lastCommandName: null, lastCommandResult: null });
      },
    };
  })
);
